<script setup>
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const { t } = useI18n()

// Variables
const isLoading = ref(true)
const route = useRoute()
const router = useRouter()
const currentPath = ref(null)

// Sidebar section variables
const personalInfoMenuItems = ref({
  name: t('settings.personal_information'),
  subItems: [
    { name: t('settings.account_settings'), route_to: '/settings/account' },
    // { name: t('settings.manage_payments'), route_to: '/settings/manage_payments' },
    { name: t('settings.saved_locations'), route_to: '/settings/saved_locations' },
    { name: t('my_garage.my_garage'), route_to: '/my_garage' }
  ]
})
const personalInfoMenuOpen = ref(false)
const myRemindersMenuItems = ref({ name: t('settings.my_reminders') })
const myRemindersMenuOpen = ref(false)
const policiesMenuItems = ref({ name: t('settings.policies') })
const policiesMenuOpen = ref(false)
const notificationSettingMenuItems = ref({
  name: t('settings.notification_settings'),
  subItems: [
    { name: t('settings.notification_settings_section.name'), route_to: '/settings/notification_settings' }
  ]
})
const notificationSettingMenuOpen = ref(false)
const billingMenuItems = ref({
  name: t('settings.billing'),
  subItems: [
    { name: t('settings.orders.name'), route_to: '/settings/orders' },
    { name: t('settings.quote_history.name'), route_to: '/settings/quote_history' }
  ]
})
const billingMenuOpen = ref(false)

onMounted(async () => {
  currentPath.value = route.path
  setCurrentActiveLink()
})

watch(() => route.path, (path) => {
  currentPath.value = path
  setCurrentActiveLink()
})

const setCurrentActiveLink = () => {
  // This will just expand the relevant section if the current path matches the link in the subitem section
  if (personalInfoMenuItems.value.subItems.find(subItem => subItem.route_to === currentPath.value)) {
    personalInfoMenuOpen.value = true
  }
  if (notificationSettingMenuItems.value.subItems.find(subItem => subItem.route_to === currentPath.value)) {
    notificationSettingMenuOpen.value = true
  }
  if (billingMenuItems.value.subItems.find(subItem => subItem.route_to === currentPath.value)) {
    billingMenuOpen.value = true
  }
}

const routeToPath = (path) => {
  router.push({ path: path })
  // currentPath.value = path
  setCurrentActiveLink()
}
</script>

<template>
  <div class="vh-screen flex flex-col">
    <!-- Personal Information -->
    <div class="menu">
      <div @click="personalInfoMenuOpen = !personalInfoMenuOpen" class="menu-item p-6 cursor-pointer flex items-center">
        <span class="text-xl font-semibold mr-2">{{ personalInfoMenuItems.name }}</span>
        <span v-if="personalInfoMenuItems.subItems" class="transform transition-transform duration-300" :class="{'rotate-180': personalInfoMenuOpen}"><img
          src="/src/assets/icons/keyboard_arrow_down_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Image"></span>
      </div>
      <Transition name="slide-down">
        <div v-if="personalInfoMenuOpen && personalInfoMenuItems.subItems" class="submenu-container">
          <div v-for="subItem in personalInfoMenuItems.subItems" @click="routeToPath(subItem.route_to)"
               :key="subItem.route_to"
               :class="`${currentPath === subItem.route_to ? 'active' : ''} menu-sub-item text-md cursor-pointer`">
            <div class="py-3 px-6">
              <span class="py-3 px-6">{{ subItem.name }}</span>
            </div>
          </div>
        </div>
      </Transition>
    </div>

    <!-- Notification Settings -->
    <div class="menu">
      <div @click="notificationSettingMenuOpen = !notificationSettingMenuOpen"
           class="menu-item p-6 cursor-pointer flex items-center">
        <span class="text-xl font-semibold mr-2">{{ notificationSettingMenuItems.name }}</span>
        <span v-if="notificationSettingMenuItems.subItems" class="transform transition-transform duration-300"
              :class="{'rotate-180': notificationSettingMenuOpen}">
          <img src="/src/assets/icons/keyboard_arrow_down_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Image">
        </span>
      </div>
      <Transition name="slide-down">
        <div v-if="notificationSettingMenuOpen && notificationSettingMenuItems.subItems" class="submenu-container">
          <div v-for="subItem in notificationSettingMenuItems.subItems"
               @click="routeToPath(subItem.route_to)"
               :key="subItem.route_to"
               :class="`${currentPath === subItem.route_to ? 'active' : ''} menu-sub-item text-md cursor-pointer`">
            <div class="py-3 px-6">
              <span class="py-3 px-6">{{ subItem.name }}</span>
            </div>
          </div>
        </div>
      </Transition>
    </div>

    <div class="menu">
      <div @click="billingMenuOpen = !billingMenuOpen"
           class="menu-item p-6 cursor-pointer flex items-center">
        <span class="text-xl font-semibold mr-2">{{ billingMenuItems.name }}</span>
        <span v-if="billingMenuItems.subItems" class="transform transition-transform duration-300"
              :class="{'rotate-180': billingMenuOpen}">
          <img src="/src/assets/icons/keyboard_arrow_down_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Image">
        </span>
      </div>
      <Transition name="slide-down">
        <div v-if="billingMenuOpen && billingMenuItems.subItems" class="submenu-container">
          <div v-for="subItem in billingMenuItems.subItems"
               @click="routeToPath(subItem.route_to)"
               :key="subItem.route_to"
               :class="`${currentPath === subItem.route_to ? 'active' : ''} menu-sub-item text-md cursor-pointer`">
            <div class="py-3 px-6">
              <span class="py-3 px-6">{{ subItem.name }}</span>
            </div>
          </div>
        </div>
      </Transition>
    </div>

    <!-- Orders -->
<!--    <div class="generic-text-button">-->
<!--      <div class="menu-item p-6 cursor-pointer flex items-center"-->
<!--           :class="{'active': currentPath === '/settings/orders'}">-->
<!--        <div>-->
<!--          <RouterLink class="text-xl font-semibold mr-2" to="/settings/orders">{{ t('settings.orders.name') }}-->
<!--          </RouterLink>-->
<!--        </div>-->
<!--      </div>-->
<!--    </div>-->
  </div>
</template>


<style lang="scss" scoped>
@import '@/assets/styles/views/settings/settings_sidebar';
</style>
